import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Mail,
  MessageCircle,
  Send,
  Phone,
  CheckCircle2,
  X,
  Copy,
  AlertOctagon,
  Plus,
  Sparkles,
} from "lucide-react";
import api, { formatErr } from "../lib/api";
import { phoneToWa, mailtoUrl } from "../lib/contact";
import Modal from "../components/Modal";
import DateTimePicker from "../components/DateTimePicker";
import VoiceTextarea from "../components/VoiceTextarea";
import { usePersistedState } from "../lib/persisted";

const KINDS = {
  email: { label: "Email", icon: Mail, color: "#A78BFA" },
  whatsapp: { label: "WhatsApp", icon: MessageCircle, color: "#22C55E" },
  sms: { label: "SMS", icon: Send, color: "#3B82F6" },
  call: { label: "Call", icon: Phone, color: "#10B981" },
};

const RANGES = [
  { v: "due", label: "Due now" },
  { v: "week", label: "Next 7 days" },
  { v: "month", label: "Next 30 days" },
  { v: "all", label: "All upcoming" },
  { v: "sent", label: "Sent" },
];

export default function Followups() {
  const [followups, setFollowups] = useState([]);
  const [leads, setLeads] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [range, setRange] = usePersistedState("followups.range", "week");
  const [active, setActive] = useState(null); // selected followup for "send" modal
  const [openSettings, setOpenSettings] = useState(false);

  const load = async () => {
    const [f, l, c] = await Promise.all([
      api.get("/followups"),
      api.get("/leads"),
      api.get("/customers"),
    ]);
    setFollowups(Array.isArray(f.data) ? f.data : []);
    setLeads(Array.isArray(l.data) ? l.data : []);
    setCustomers(Array.isArray(c.data) ? c.data : []);
  };
  useEffect(() => {
    load();
  }, []);

  const lookupLead = (id) => leads.find((l) => l.id === id);
  const lookupCustomer = (id) => customers.find((c) => c.id === id);

  const filtered = useMemo(() => {
    const now = Date.now();
    const week = 7 * 86400000;
    const month = 30 * 86400000;
    return followups
      .filter((f) => {
        const due = new Date(f.due_date).getTime();
        if (range === "sent") return f.status === "sent";
        if (f.status !== "pending") return false;
        if (range === "due") return due <= now;
        if (range === "week") return due <= now + week;
        if (range === "month") return due <= now + month;
        return true; // all upcoming
      })
      .sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
  }, [followups, range]);

  const dueNowCount = followups.filter((f) => f.status === "pending" && new Date(f.due_date).getTime() <= Date.now()).length;
  const upcomingCount = followups.filter((f) => f.status === "pending").length;
  const sentCount = followups.filter((f) => f.status === "sent").length;

  return (
    <div className="space-y-5" data-testid="followups-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#647087]">Re-engage</div>
          <h1 className="font-display text-2xl md:text-4xl font-bold tracking-tight mt-1 flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-fuchsia-400 animate-pulse" />
            Follow-ups
          </h1>
        </div>
        <button
          onClick={() => setOpenSettings(true)}
          className="hv-btn hv-btn-ghost"
          data-testid="followups-cadence-btn"
        >
          <Sparkles className="w-4 h-4" /> Cadence template
        </button>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <Kpi label="Due now" value={dueNowCount} accent="#F43F5E" pulse={dueNowCount > 0} />
        <Kpi label="Upcoming" value={upcomingCount} accent="#A78BFA" />
        <Kpi label="Sent total" value={sentCount} accent="#10B981" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {RANGES.map((r) => (
          <button
            key={r.v}
            onClick={() => setRange(r.v)}
            data-testid={`followups-range-${r.v}`}
            className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-full border transition ${
              range === r.v
                ? "border-[#1fb6c1] text-[#1fb6c1] bg-[#1fb6c1]/10"
                : "border-white/10 text-[#647087] hover:text-white"
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>

      <div className="hv-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0a0b0e] border-b border-white/10">
            <tr className="text-left text-[#647087] text-xs uppercase tracking-wider">
              <th className="px-4 py-3">Due</th>
              <th className="px-4 py-3">Lead</th>
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3">Channel</th>
              <th className="px-4 py-3">Subject</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((f) => {
              const k = KINDS[f.kind] || KINDS.email;
              const Icon = k.icon;
              const lead = lookupLead(f.lead_id);
              const cust = lookupCustomer(f.customer_id);
              const overdue = f.status === "pending" && new Date(f.due_date) < new Date();
              return (
                <tr
                  key={f.id}
                  className="border-b border-white/5 hover:bg-white/[0.03] transition"
                  data-testid={`followups-row-${f.id}`}
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div
                      className={`text-xs font-bold ${
                        overdue ? "text-rose-300" : "text-[#dde3ec]"
                      }`}
                    >
                      {new Date(f.due_date).toLocaleDateString('en-GB')}
                    </div>
                    {overdue && (
                      <div className="text-[10px] uppercase tracking-wider text-rose-400">
                        Overdue
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[#dde3ec]">{lead?.title || "—"}</td>
                  <td className="px-4 py-3">
                    {cust ? (
                      <Link
                        to={`/customers/${cust.id}`}
                        className="text-[#1fb6c1] hover:underline"
                      >
                        {cust.name}
                      </Link>
                    ) : (
                      <span className="text-[#647087]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider px-2 py-1 rounded-full font-bold"
                      style={{
                        background: `${k.color}1a`,
                        color: k.color,
                        border: `1px solid ${k.color}66`,
                      }}
                    >
                      <Icon className="w-3 h-3" /> {k.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[#a0aabf] max-w-md truncate">{f.subject || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    {f.status === "pending" ? (
                      <button
                        onClick={() => setActive({ followup: f, lead, customer: cust })}
                        className="hv-btn hv-btn-primary text-xs"
                        data-testid={`followups-send-${f.id}`}
                      >
                        Send <Send className="w-3 h-3" />
                      </button>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wider text-emerald-300">Sent</span>
                    )}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-[#647087] text-sm">
                  Nothing in this range. Move a lead to <span className="text-[#1fb6c1]">Future prospect</span> to seed cadence steps.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {active && (
        <SendModal
          followup={active.followup}
          lead={active.lead}
          customer={active.customer}
          onClose={() => setActive(null)}
          onDone={() => {
            setActive(null);
            load();
          }}
        />
      )}
      {openSettings && (
        <CadenceTemplateModal onClose={() => setOpenSettings(false)} />
      )}
    </div>
  );
}

function Kpi({ label, value, accent, pulse }) {
  return (
    <div
      className={`hv-card p-4 flex items-center justify-between ${pulse ? "animate-pulse" : ""}`}
      style={{ borderColor: `${accent}40`, background: `${accent}08` }}
    >
      <div className="text-xs uppercase tracking-wider text-[#a0aabf]">{label}</div>
      <div className="font-display text-3xl font-bold" style={{ color: accent }}>
        {value}
      </div>
    </div>
  );
}

function SendModal({ followup, lead, customer, onClose, onDone }) {
  const k = KINDS[followup.kind] || KINDS.email;
  const Icon = k.icon;
  const [body, setBody] = useState(followup.body || "");
  const [busy, setBusy] = useState(false);

  const tel = customer?.phone ? customer.phone.replace(/[^0-9+]/g, "") : "";
  const wa = customer?.whatsapp ? customer.whatsapp.replace(/[^0-9]/g, "") : "";
  const email = customer?.email || "";

  const copy = () => navigator.clipboard.writeText(body);

  const markSent = async () => {
    setBusy(true);
    try {
      await api.post(`/followups/${followup.id}/sent`, { notes: body });
      onDone();
    } finally {
      setBusy(false);
    }
  };
  const skip = async () => {
    const reason = window.prompt("Skip reason (optional)") || "";
    await api.post(`/followups/${followup.id}/skip`, { reason });
    onDone();
  };

  return (
    <Modal open onClose={onClose} title="Send follow-up" testId="followups-send-modal" maxWidth="max-w-2xl">
      <div className="space-y-3">
        <div className="text-sm flex items-center gap-2">
          <span
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider px-2 py-1 rounded-full font-bold"
            style={{ background: `${k.color}1a`, color: k.color, border: `1px solid ${k.color}66` }}
          >
            <Icon className="w-3 h-3" /> {k.label}
          </span>
          {customer && (
            <span className="text-[#a0aabf]">
              to <span className="text-[#1fb6c1]">{customer.name}</span>
            </span>
          )}
          {lead && <span className="text-[#647087]"> · {lead.title}</span>}
        </div>

        {followup.subject && (
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Subject</label>
            <div className="hv-input mt-1 opacity-80">{followup.subject}</div>
          </div>
        )}

        <div>
          <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Message</label>
          <VoiceTextarea
            value={body}
            onChange={setBody}
            rows={8}
            className="mt-1 leading-relaxed"
            context="lead_activity"
            testIdPrefix="followups-send-body-voice"
            data-testid="followups-send-body"
          />
        </div>

        {/* Quick actions */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={copy}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="followups-copy-btn"
          >
            <Copy className="w-3.5 h-3.5" /> Copy
          </button>
          {followup.kind === "whatsapp" && wa && (
            <a
              href={`https://wa.me/${wa}?text=${encodeURIComponent(body)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="hv-btn hv-btn-ghost text-xs"
              style={{ borderColor: "#22C55E66", color: "#22C55E" }}
              data-testid="followups-open-wa"
            >
              <MessageCircle className="w-3.5 h-3.5" /> Open WhatsApp
            </a>
          )}
          {followup.kind === "email" && email && (
            <a
              href={mailtoUrl({
                to: email,
                subject: followup.subject || "",
                body,
              })}
              className="hv-btn hv-btn-ghost text-xs"
              style={{ borderColor: "#A78BFA66", color: "#A78BFA" }}
              data-testid="followups-open-email"
              title="Open in your mail app"
            >
              <Mail className="w-3.5 h-3.5" /> Open mail
            </a>
          )}
          {followup.kind === "sms" && tel && (
            <a
              href={phoneToWa(tel, { text: body }) || "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="hv-btn hv-btn-ghost text-xs"
              style={{ borderColor: "#3B82F666", color: "#3B82F6" }}
              title="Send via WhatsApp"
            >
              <Send className="w-3.5 h-3.5" /> Open WhatsApp
            </a>
          )}
          {followup.kind === "call" && tel && (
            <a
              href={phoneToWa(tel) || "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="hv-btn hv-btn-ghost text-xs"
              style={{ borderColor: "#10B98166", color: "#10B981" }}
              title="Call (opens WhatsApp)"
            >
              <Phone className="w-3.5 h-3.5" /> Call now
            </a>
          )}
        </div>

        <div className="flex items-center gap-2 pt-2">
          <button
            onClick={markSent}
            disabled={busy}
            className="hv-btn hv-btn-primary flex-1"
            data-testid="followups-mark-sent"
          >
            <CheckCircle2 className="w-4 h-4" /> {busy ? "Saving…" : "Mark as sent"}
          </button>
          <button onClick={skip} className="hv-btn hv-btn-ghost text-rose-300" data-testid="followups-skip">
            <X className="w-4 h-4" /> Skip
          </button>
        </div>

        <div className="text-[10px] text-[#647087]">
          🔒 Sending is recorded as an outbound activity in the customer's call log.
        </div>
      </div>
    </Modal>
  );
}

function CadenceTemplateModal({ onClose }) {
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/settings/followup_cadence").then((r) => {
      setSteps(r.data.steps || []);
      setLoading(false);
    });
  }, []);

  const update = (i, patch) => {
    setSteps((prev) => prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  };
  const add = () => {
    setSteps((prev) => [
      ...prev,
      { offset_days: 7, kind: "email", subject: "", body: "" },
    ]);
  };
  const remove = (i) => setSteps((prev) => prev.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      const cleaned = steps
        .filter((s) => s.kind && (s.subject || s.body))
        .map((s) => ({
          offset_days: Number(s.offset_days) || 0,
          kind: s.kind,
          subject: s.subject || "",
          body: s.body || "",
        }));
      await api.put("/settings/followup_cadence", { steps: cleaned });
      onClose();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Modal open onClose={onClose} title="Cadence template" testId="cadence-modal" maxWidth="max-w-3xl">
        <div className="text-sm text-[#647087]">Loading…</div>
      </Modal>
    );
  }

  return (
    <Modal open onClose={onClose} title="Future-Prospect cadence template" testId="cadence-modal" maxWidth="max-w-3xl">
      <div className="space-y-3" data-testid="cadence-form">
        <div className="text-xs text-[#a0aabf]">
          When a lead is moved to <span className="text-[#1fb6c1]">Future prospect</span>, a
          follow-up is auto-scheduled for each step here. Each step's <em>offset_days</em> is
          relative to the moment of phase change. Steps support 4 channels — email, WhatsApp, SMS, call.
        </div>
        {steps.length === 0 && (
          <div className="hv-card p-6 text-center text-sm text-[#647087]">
            No cadence configured yet. Add a few steps below.
          </div>
        )}
        {steps.map((s, i) => {
          const k = KINDS[s.kind] || KINDS.email;
          return (
            <div
              key={i}
              className="hv-card p-3 space-y-2"
              data-testid={`cadence-step-${i}`}
              style={{ borderColor: `${k.color}33` }}
            >
              <div className="grid grid-cols-12 gap-2 items-start">
                <div className="col-span-2">
                  <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">Days</label>
                  <input
                    type="number"
                    value={s.offset_days}
                    onChange={(e) => update(i, { offset_days: e.target.value })}
                    className="hv-input mt-1"
                    data-testid={`cadence-offset-${i}`}
                  />
                </div>
                <div className="col-span-3">
                  <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">Channel</label>
                  <select
                    value={s.kind}
                    onChange={(e) => update(i, { kind: e.target.value })}
                    className="hv-input mt-1"
                    data-testid={`cadence-kind-${i}`}
                  >
                    {Object.entries(KINDS).map(([v, k]) => (
                      <option key={v} value={v}>
                        {k.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-span-7">
                  <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">
                    Subject (email/sms only)
                  </label>
                  <input
                    value={s.subject || ""}
                    onChange={(e) => update(i, { subject: e.target.value })}
                    className="hv-input mt-1"
                    placeholder="e.g. Quick check-in on our quote"
                    data-testid={`cadence-subject-${i}`}
                  />
                </div>
              </div>
              <VoiceTextarea
                value={s.body || ""}
                onChange={(v) => update(i, { body: v })}
                placeholder="Message template — keep it human and short. Or tap the mic."
                rows={4}
                context="lead_activity"
                testIdPrefix={`cadence-body-voice-${i}`}
                data-testid={`cadence-body-${i}`}
              />
              <div className="flex justify-end">
                <button
                  onClick={() => remove(i)}
                  className="hv-btn hv-btn-ghost text-xs text-rose-300"
                  data-testid={`cadence-remove-${i}`}
                >
                  <X className="w-3 h-3" /> Remove step
                </button>
              </div>
            </div>
          );
        })}
        <button
          onClick={add}
          className="hv-btn hv-btn-ghost w-full"
          data-testid="cadence-add-step"
        >
          <Plus className="w-4 h-4" /> Add step
        </button>
        {err && (
          <div className="text-xs text-rose-300 inline-flex items-center gap-1">
            <AlertOctagon className="w-3 h-3" /> {err}
          </div>
        )}
        <button
          onClick={save}
          disabled={saving}
          className="hv-btn hv-btn-primary w-full"
          data-testid="cadence-save"
        >
          {saving ? "Saving…" : "Save cadence"}
        </button>
      </div>
    </Modal>
  );
}
